import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useCurrentViewPropertySession } from 'src/hooks/use-current-view-property-session';
import Breadcrumbs from '~components/breadcrumbs';
import Typography from '~components/typography';
import { getAcademicYearType, haveAcademicYearsOnThisProperty } from '~utils/use-academic-year';
import AcademicYearSelect from './academic-year-select';
import GroupAcademicYearSelect from './group-academic-year-select';
import LandlordSelect from './landlord-select';
import PropertySelect from './property-select';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: '8px 32px 0px',
  },

  header: {
    ...theme.flex.crossCenter,
    paddingTop: 8,
    paddingBottom: 16,
    '& > * + *': { marginLeft: 12 },
  },

  rightAction: {
    ...theme.flex.mainEnd,
    ...theme.flex.crossCenter,
    flex: 1,
  },
}));

/**
 * Simple guideline
 * https://github.com/Project-G66-Org/projectg-fe-pms/pull/67
 */
function Panel({
  academicYearSelectProps,
  propertySelectProps,
  breadcrumbs,
  classes = {},
  onAcademicYearChange: onAcademicYearChangeProp = () => {},
  onPropertyChange: onPropertyChangeProp = () => {},
  onLandLordChange: onLandLordChangeProp = () => {},
  rightAction,
  showAcademicYearSelection,
  showAcademicYearTab,
  showPropertySelection = true,
  showLandlordSelection = false,
  title,
  topAction,
}) {
  const [
    { currentViewProperty, currentViewLandLord },
    { onPropertyChange, onAcademicYearChange, onLandLordChange },
  ] = useCurrentViewPropertySession();

  const currentViewPropertyHasAcademicYears = useMemo(
    () =>
      showLandlordSelection
        ? (currentViewLandLord?.groupAcademicYears || []).length > 0
        : haveAcademicYearsOnThisProperty(currentViewProperty),
    [currentViewLandLord?.groupAcademicYears, currentViewProperty, showLandlordSelection]
  );

  const isShowAcademicYear = showAcademicYearSelection || showAcademicYearTab;

  const styles = useStyles();

  const handlePropertySelectChange = useCallback(
    ({ id }) => {
      onPropertyChange(id);
      onPropertyChangeProp(id);
    },
    [onPropertyChange, onPropertyChangeProp]
  );

  const handleAcademicYearChange = useCallback(
    (academicYear) => {
      onAcademicYearChange(academicYear.id, showLandlordSelection);
      if (!showLandlordSelection) {
        onAcademicYearChangeProp(
          getAcademicYearType(currentViewProperty.academicYear, academicYear.id),
          academicYear
        );
      }
    },
    [
      currentViewProperty.academicYear,
      onAcademicYearChange,
      onAcademicYearChangeProp,
      showLandlordSelection,
    ]
  );

  const handleGroupLevelAcademicYearChange = useCallback(
    (academicYear) => {
      onAcademicYearChange(academicYear.id, showLandlordSelection);
      onAcademicYearChangeProp(
        getAcademicYearType(currentViewProperty.academicYear, academicYear.id),
        academicYear
      );
    },
    [
      currentViewProperty.academicYear,
      onAcademicYearChange,
      onAcademicYearChangeProp,
      showLandlordSelection,
    ]
  );

  const handleLandlordSelectChange = useCallback(
    ({ id }) => {
      onLandLordChange(id);
      onLandLordChangeProp(id);
    },
    [onLandLordChange, onLandLordChangeProp]
  );

  return (
    <div className={styles.root}>
      {topAction}
      {breadcrumbs && <Breadcrumbs data={breadcrumbs} />}
      <div className={styles.header}>
        <Typography variant="h2" style={{ marginRight: 4 }}>
          {title}
        </Typography>
        {showLandlordSelection && <LandlordSelect onChange={handleLandlordSelectChange} />}
        {showPropertySelection && (
          <PropertySelect onChange={handlePropertySelectChange} {...propertySelectProps} />
        )}
        {isShowAcademicYear && showPropertySelection && currentViewPropertyHasAcademicYears && (
          <AcademicYearSelect onChange={handleAcademicYearChange} {...academicYearSelectProps} />
        )}
        {isShowAcademicYear && showLandlordSelection && currentViewPropertyHasAcademicYears && (
          <GroupAcademicYearSelect
            onChange={handleGroupLevelAcademicYearChange}
            {...academicYearSelectProps}
          />
        )}
        {rightAction && (
          <div className={classNames(styles.rightAction, classes.rightAction)}>{rightAction}</div>
        )}
      </div>
    </div>
  );
}

Panel.propTypes = {
  academicYearSelectProps: PropTypes.object,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({ path: PropTypes.string, label: PropTypes.string })
  ),
  classes: PropTypes.shape({ rightAction: PropTypes.string }),
  onAcademicYearChange: PropTypes.func,
  onPropertyChange: PropTypes.func,
  onLandLordChange: PropTypes.func,
  propertySelectProps: PropTypes.object,
  rightAction: PropTypes.node,
  showAcademicYearSelection: PropTypes.bool,
  showAcademicYearTab: PropTypes.bool,
  showNominationPlanSelection: PropTypes.bool,
  showPropertySelection: PropTypes.bool,
  showLandlordSelection: PropTypes.bool,
  title: PropTypes.string,
  topAction: PropTypes.node,
};

export default Panel;
